import { useForm } from '@tanstack/react-form'
import clsx from 'clsx'
import last from 'lodash/last'
import map from 'lodash/map'
import { useRouter } from 'next/router'
import React from 'react'

import { ampli } from '@Analytics/ampli'
import { Measure } from '@Assets/SVG'
import SvgCheckmark from '@Assets/SVG/InColor/Checkmark'
import BraceletWristSizeSelector from '@Blocks/Special/BraceletWristSizeSelector'
import { Chip } from '@Components/Chips/Chip/Chip'
import { Drawer } from '@Components/Drawer'
import { DrawerContentBasic } from '@Components/Drawer/Content'
import { DrawerPageSync } from '@Components/Drawer/Page'
import { Checkbox } from '@Components/FormElements/UI/Checkbox'
import { ImageV3, ImageV4 } from '@Components/Image'
import { InputErrors, InputGroup, InputGroupBase, InputGroupInput, InputGroupLabel, metaToFieldState } from '@Components/Input'
import { Wordbook } from '@Components/Wordbook'
import { Button } from '@CoreComponents/Buttons/Button'
import { SizeGuideFitsContext } from '@Domains/Cart/Components/Prompt/Context'
import { PromptSelectVariant } from '@Domains/Cart/Components/Prompt/types'
import { DrawerDataFragment, PagePresentation, useSubscribeToBackInStockMutation } from '@Generated/graphql'
import { Trans, useTranslation } from '@Globals/Translations'
import { getStore, useStore } from '@Globals/useStorage'
import { EmailRegexValidation } from '@Shared/Components/Forms/Validations/EmailValidation'

import styles from './Size.module.scss'

const store = getStore('wrist_size', '')

type Product = NonNullable<PromptSelectVariant['product']>
type ProductVariant = NonNullable<Product['variants']>[0]

const images = {
  necklaces: {
    '35cm': 'https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/blt301df2e172a2dadb/35cm-ID-20353.jpg',
    '40cm': 'https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/bltcfa6dfc6541639d9/40cm-ID-20352.jpg',
    '45cm': 'https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/blt2b1b99fcd9f2731f/45cm-ID-20347.jpg',
    '50cm': 'https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/blte05fc13e9f785a5f/50cm-ID-15174.jpg',
    '55cm': 'https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/bltd04398cad3528687/55cm-ID-15175.jpg',
    '60cm': 'https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/blt80494b12fd747ddb/60cm-ID-15176.jpg',
    '65cm': 'https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/bltdd02b880db18811d/65cm-ID-15177.jpg',
  },
}

const sizeGuideHandle = Drawer.createHandle()

const VariantFits = React.memo(({ fits }: { fits: ProductVariant['fits'] }) => {
  // TODO: make more generic, instead of wrist size here
  const wristSize = useStore(store)
  const key = `wrist_size__${wristSize}`

  if (!fits || fits.length === 0) {
    return null
  }

  return (
    <div className={styles.fits}>
      {fits.map((item) => {
        if (item.dimension !== key) {
          return null
        }

        const isNormalFit = item.id?.endsWith('normal-fit')

        return (
          <Chip
            key={item.id}
            backgroundColor={isNormalFit ? 'var(--green-500)' : 'var(--neutral-200)'}
            textColor={isNormalFit ? 'var(--white)' : 'var(--black)'}
          >
            <Wordbook wordbook={item.fit} />
          </Chip>
        )
      })}
    </div>
  )
})

const getIsInStock = (variant: ProductVariant | undefined | null) => !!variant?.availability?.isInStock

const SubscribeBackInStockDrawer = ({ sizeLabel, variantID }: { sizeLabel: string, variantID: string }) => {
  const { t } = useTranslation()
  const id = React.useId()
  const [subscribeToBackInStock] = useSubscribeToBackInStockMutation()
  const [submitError, setSubmitError] = React.useState<string | null>(null)

  const form = useForm({
    defaultValues: { email: '' },
    onSubmit: async ({ value }) => {
      let isSuccess = false

      try {
        await subscribeToBackInStock({
          variables: {
            email: value.email,
            productID: variantID,
          },
        })

        isSuccess = true
      } catch {
        setSubmitError(t('common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US'))
      } finally {
        ampli.productOutOfStockOptInRes({
          is_success: isSuccess,
          is_size_product: true,
          is_email_added: !!value.email,
          is_phone_number_added: false,
        })
      }

      if (!isSuccess) {
        // Throw so the form stays in a non-successful state and the success message stays hidden.
        throw new Error('subscribe_to_back_in_stock_failed')
      }
    },
  })

  const emailValidator = ({ value }: { value: string }) => {
    if (EmailRegexValidation(value)) {
      return undefined
    }

    return t('common:COF_YOUR_EMAIL_IS_INVALID')
  }

  return (
    <DrawerPageSync
      page={{
        id: 'subscribe_back_in_stock',
        title: t('cms:SIZE_IS_OUT_OF_STOCK'),
        type: 'custom',
      } as DrawerDataFragment}
      presentation={PagePresentation.Sidebar}
      sourceID="subscribe_back_in_stock"
      triggerRender={props => (
        <button {...props} className={styles.stockUpdate} type="button">
          {t('cms:NOTIFY_ME')}
        </button>
      )}
    >
      {/* The drawer content renders through a portal, so click events still bubble up the React
          tree into SizeOption's onClick (which calls preventDefault, cancelling the form submit).
          Stop propagation here so submit/selection clicks stay contained to this form. */}
      <div className={styles.backInStock} onClick={e => e.stopPropagation()} role="presentation">
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            setSubmitError(null)
            form.handleSubmit().catch(() => {
              // The failure is surfaced via submitError; swallow to avoid an unhandled rejection.
            })
          }}
        >
          <p className={styles.text}>{t('cms:WANT_TO_BE_NOTIFIED_WHEN_SIZE_IS_AVAILABLE_AGAIN')}</p>
          <p className={styles.text}>
            <strong>
              {t('cms:SIZE')}
              :
            </strong>
            {' '}
            {sizeLabel}
          </p>

          <form.Field name="email" validators={{ onSubmit: emailValidator }}>
            {field => (
              <div>
                <InputGroup state={metaToFieldState(field.state.meta, field.state.value)}>
                  <InputGroupBase>
                    <InputGroupLabel htmlFor={`${field.name}-${id}`}>
                      {`${t('common:EMAIL_ADDRESS')} *`}
                    </InputGroupLabel>
                    <InputGroupInput
                      aria-invalid={field.state.meta.isValid === false && field.state.meta.isTouched}
                      autoComplete="email"
                      id={`${field.name}-${id}`}
                      name={field.name}
                      onBlur={field.handleBlur}
                      onChange={e => field.handleChange(e.target.value.trim())}
                      type="email"
                      value={field.state.value}
                    />
                  </InputGroupBase>
                </InputGroup>
                <InputErrors
                  errors={field.state.meta.errors}
                  isTouched={field.state.meta.isTouched}
                  isValid={field.state.meta.isValid}
                />
              </div>
            )}
          </form.Field>

          <form.Subscribe selector={state => state.isSubmitting}>
            {isSubmitting => (
              <Button fillWidth innerSpacing="large" loading={isSubmitting} theme="black" type="submit">
                {t('cms:NOTIFY_ME')}
              </Button>
            )}
          </form.Subscribe>

          <form.Subscribe selector={state => state.isSubmitSuccessful}>
            {isSubmitSuccessful => (
              isSubmitSuccessful && (
                <div className={styles.success}>
                  <SvgCheckmark />
                  <p>{t('cms:PP_OUT_OF_STOCK_OVERLAY_CONTACT_YOU_SOON')}</p>
                </div>
              )
            )}
          </form.Subscribe>

          {submitError && <p className={styles.error}>{submitError}</p>}
        </form>
      </div>
    </DrawerPageSync>
  )
}

/* eslint-disable react/destructuring-assignment */
const SizeOption = ({
  index,
  onSelect,
  productType,
  productVariant,
  selectedVariantID,
}: {
  index: number
  onSelect: (variantID: string) => void
  productType: Product['type']
  productVariant: ProductVariant
  selectedVariantID: string | null
}) => {
  const { locale = 'en-GB' } = useRouter()
  const { t } = useTranslation()

  const isInStock = !!productVariant.availability?.isInStock
  const quantity = productVariant.availability?.quantity || 0
  // @ts-ignore
  const restock = productVariant.restock?.value?.key || 'unknown'
  // @ts-ignore
  const sizeKey = productVariant?.size?.value?.key || `unknown-${index}`
  // @ts-ignore
  const sizeLabel = productVariant?.size?.value?.label?.[locale] || productVariant?.size?.value?.key || 'Unknown'

  const onClick = React.useCallback((event: React.MouseEvent<HTMLDivElement | HTMLInputElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (!isInStock) {
      return
    }

    onSelect(productVariant.id || '')
  }, [productVariant.id, isInStock, onSelect])

  if (
    restock !== 'enabled'
    && (!isInStock || quantity === 0)
  ) {
    return null
  }

  const optionImageSize = sizeKey.replaceAll(' ', '')
  const optionImageURL = images[productType || '']?.[optionImageSize]

  return (
    <div
      aria-disabled={!isInStock}
      className={clsx(
        styles.option,
        !isInStock && styles.unavailable,
        optionImageURL ? styles.withImage : styles.withoutImage,
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      {optionImageURL && (
        <ImageV3
          alt={optionImageSize}
          className={clsx(styles.optionImage, !isInStock && styles.disabled)}
          draggable={false}
          height={66}
          src={optionImageURL}
          width={100}
        />
      )}

      <Checkbox
        aria-label={sizeLabel}
        checked={productVariant.id === selectedVariantID}
        className={styles.checkboxLabel}
        disabled={!isInStock}
        id={`sizeSelect${sizeKey}`}
        label=""
        labelClassName={styles.checkbox}
        onClick={onClick}
        rounded
      />

      <div className={styles.body}>
        <div className={styles.text}>
          <div className={styles.label}>{sizeLabel}</div>

          {(isInStock && quantity < 10) && (
            <div className={styles.stock}>
              {`${quantity} ${t('products:PP_LEFT_IN_STOCK_LABEL')}`}
            </div>
          )}

          {!isInStock && (
            <div className={styles.outOfStock}>
              <div className={styles.stock}>
                {t('cms:OUT_OF_STOCK')}
              </div>

              {productVariant.id && (
                <>
                  <span aria-hidden className={styles.separator}>-</span>
                  <SubscribeBackInStockDrawer sizeLabel={sizeLabel} variantID={productVariant.id} />
                </>
              )}
            </div>
          )}
        </div>

        <VariantFits fits={productVariant.fits} />
      </div>
    </div>
  )
}
/* eslint-enable react/destructuring-assignment */

const VariantSelector = ({
  productType,
  selectedVariantID,
  setSelectedVariantID,
  variants,
}: {
  productType: Product['type']
  selectedVariantID: string | null
  setSelectedVariantID: (variantID: string) => void
  variants: Product['variants']
}) => {
  if (!variants) {
    return null
  }

  return (
    <div className={styles.content}>
      {map(variants, (option, index) => (
        <SizeOption
          key={option.id}
          index={index}
          onSelect={(variantID: string) => setSelectedVariantID(variantID)}
          productType={productType}
          productVariant={option}
          selectedVariantID={selectedVariantID}
        />
      ))}
    </div>
  )
}

const HelpText = ({ dimensions, variants }: { dimensions: Product['sizeGuideItems'], variants: Product['variants'] }) => {
  const { t } = useTranslation()
  // TODO: make more generic, instead of wrist size here
  const wristSize = useStore(store)
  const key = `wrist_size__${wristSize}`

  const hasMatchingFits = React.useMemo(() => {
    if (!wristSize) {
      return null
    }

    if (!variants || variants.length === 0) {
      return false
    }

    return variants.some((variant) => {
      if (!variant.fits || variant.fits.length === 0) {
        return false
      }

      return variant.fits.some(item => item.dimension === key)
    })
  }, [key, variants, wristSize])

  if (!dimensions || dimensions.length === 0 || !wristSize) {
    return null
  }

  return (
    <div className={styles.fitHelpText}>
      <div>
        <Trans i18nKey="cms:fit_help_text" values={{ context: 'wrist', size: wristSize }} />
        {' '}
        <BraceletWristSizeSelector
          triggerRender={props => (
            <button
              {...props}
              className={styles.fitHelpButton}
              type="button"
            >
              {t('cms:fit_help_edit', { context: 'wrist' })}
            </button>
          )}
        />
      </div>
      {hasMatchingFits === false && (
        <div className={styles.noFits}>
          {t('cms:fit_help_no_fit', { context: 'wrist' })}
        </div>
      )}
    </div>
  )
}

const SizeGuideDrawer = ({ page }: { page: Product['sizeGuide'] }) => {
  const [open, onOpenChange] = React.useState(false)
  const { t } = useTranslation()

  if (!page) {
    return null
  }

  return (
    <div className={styles.sizeGuide}>
      <DrawerPageSync
        handle={sizeGuideHandle}
        onOpenChange={onOpenChange}
        open={open}
        page={page as DrawerDataFragment}
        presentation={PagePresentation.Sidebar}
        sourceID="size_prompt"
        triggerRender={props => (
          <Button
            {...props}
            className=""
            fillWidth
            innerSpacing="medium"
            theme="white"
            type="button"
          >
            <Measure />
            {t('cms:SIZE_GUIDE_LABEL')}
          </Button>
        )}
      />
    </div>
  )
}

export const SizeDrawer = ({
  loading,
  onSubmit,
  open,
  product,
  promptSelectVariants = [],
  title,
  ...props
}: React.ComponentProps<typeof Drawer> & {
  loading: boolean
  onSubmit: (variantID: string) => void
  product: NonNullable<PromptSelectVariant['product']>
  promptSelectVariants?: PromptSelectVariant[]
  /** Overrides the default "Choose size" drawer title (e.g. with a progress stepper). */
  title?: React.ReactNode
}) => {
  const { t } = useTranslation()

  const [entered, setEntered] = React.useState(false)

  const [currentStepIndex, isLastStep, showStepper] = React.useMemo(() => {
    const show = promptSelectVariants && promptSelectVariants?.length > 1
    const i = promptSelectVariants!.findIndex(p => p.productID === product.id)
    const isLast = last(promptSelectVariants)?.productID === product.id
    return [i, isLast, show]
  }, [product.id, promptSelectVariants])

  const [selectedVariantID, setSelectedVariantID] = React.useState<string | null>(null)
  const [showError, setShowError] = React.useState(false)

  const selectedVariant = product.variants?.find(variant => variant.id === selectedVariantID)
  const noneSelected = selectedVariantID === null
  const sizeNotInStock = !getIsInStock(selectedVariant)

  React.useEffect(() => {
    setEntered(true)
    ampli.promptOpen({ source_id: showStepper ? 'size_prompt_grouped' : 'size_prompt' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sizeGuideFitsValue = React.useMemo(() => ({
    fits: product.sizeGuideFits || [],
    items: product.sizeGuideItems || [],
    onChangeSize: () => sizeGuideHandle.close(),
  }), [product.sizeGuideFits, product.sizeGuideItems])

  const deactivateAddToCart = loading || noneSelected || !selectedVariant || sizeNotInStock

  return (
    <SizeGuideFitsContext.Provider value={sizeGuideFitsValue}>
      <Drawer open={open ? entered : open} triggerRender={false} {...props}>
        <DrawerContentBasic
          footer={(
            <div className={styles.footer}>
              {deactivateAddToCart && showError && (
                <p className={styles.error}>{t('cms:USER_SELECT_OVERLAY_SIZE_TITLE')}</p>
              )}

              <Button
                disabled={deactivateAddToCart}
                fillWidth
                innerSpacing="medium"
                loading={loading}
                onClick={() => {
                  if (deactivateAddToCart) {
                    setShowError(true)
                  } else {
                    setShowError(false)
                    onSubmit(selectedVariantID)
                  }
                }}
                type="button"
              >
                {!showStepper && t('cms:ADD_TO_CART')}
                {showStepper && isLastStep && t('cms:ADD_TO_CART')}
                {showStepper && !isLastStep && t('common:PAGINATION_NEXT_PAGE')}
              </Button>
            </div>
          )}
          title={title || (
            <>
              {t('products:CHOOSE_SIZE')}
              {showStepper && ` (${currentStepIndex + 1}/${promptSelectVariants?.length})`}
            </>
          )}
        >
          <div className={styles.sizePrompt}>
            {showStepper && !!promptSelectVariants[currentStepIndex]?.product?.primaryAsset && (
              <div className={styles.currentStep}>
                <div className={styles.imageWrapper}>
                  <ImageV4 src={promptSelectVariants[currentStepIndex].product!.primaryAsset!.url} width={85} />
                </div>
                <p>{promptSelectVariants[currentStepIndex].product?.name}</p>
              </div>
            )}

            <SizeGuideDrawer page={product.sizeGuide} />

            <HelpText dimensions={product.sizeGuideItems} variants={product.variants} />

            <VariantSelector
              productType={product.type}
              selectedVariantID={selectedVariantID}
              setSelectedVariantID={setSelectedVariantID}
              variants={product.variants}
            />
          </div>
        </DrawerContentBasic>
      </Drawer>
    </SizeGuideFitsContext.Provider>
  )
}
